import { useState, useEffect } from 'react';
import { ItechnikUser } from '../../types/type';
import { AutoparkService, IAutopark } from '../../services/autopark.service';
import './technicAutoparks.css';

function TechnicAutoparks({ technic }: { technic: ItechnikUser }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoparks, setAutoparks] = useState<IAutopark[]>([]);
  const [availableAutoparks, setAvailableAutoparks] = useState<IAutopark[]>([]);
  const [selectedAutoparkId, setSelectedAutoparkId] = useState<string>('');

  useEffect(() => {
    const fetchAutoparks = async () => {
      try {
        setLoading(true);
        setError(null);

        // Получаем автопарки техника
        const technicAutoparks = await AutoparkService.getTechnicAutoparks(technic.id);
        setAutoparks(technicAutoparks);

        // Получаем доступные автопарки для добавления
        const availableAutoparks = await AutoparkService.getAvailableAutoparksForProfile(technic.id, 'technik');
        setAvailableAutoparks(availableAutoparks);
      } catch (error: any) {
        console.error('Ошибка при загрузке автопарков:', error);
        setError(error.message || 'Не удалось загрузить автопарки');
        setAutoparks([]);
        setAvailableAutoparks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAutoparks();
  }, [technic.id]);

  const handleAddAutopark = async () => {
    if (!selectedAutoparkId) return;

    try {
      setLoading(true);

      // Привязываем автопарк к технику
      await AutoparkService.linkProfileToAutopark(selectedAutoparkId, technic.id);

      // Обновляем список автопарков
      const selectedAutopark = availableAutoparks.find((ap) => ap.id === selectedAutoparkId);
      if (selectedAutopark) {
        setAutoparks([...autoparks, selectedAutopark]);
        setAvailableAutoparks(availableAutoparks.filter((ap) => ap.id !== selectedAutoparkId));
      }

      setSelectedAutoparkId('');
    } catch (error: any) {
      console.error('Ошибка при добавлении автопарка:', error);
      setError(error.message || 'Не удалось добавить автопарк');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAutopark = async (autoparkId: string) => {
    try {
      setLoading(true);

      // Отвязываем автопарк от техника
      await AutoparkService.unlinkProfileFromAutopark(autoparkId, technic.id);

      // Обновляем списки автопарков
      const removedAutopark = autoparks.find((ap) => ap.id === autoparkId);
      if (removedAutopark) {
        setAutoparks(autoparks.filter((ap) => ap.id !== autoparkId));
        setAvailableAutoparks([...availableAutoparks, removedAutopark]);
      }
    } catch (error: any) {
      console.error('Ошибка при удалении автопарка:', error);
      setError(error.message || 'Не удалось удалить автопарк');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="technic-autoparks">
      <div className="technic-autoparks__info">
        <p>
          {technic.autoparkEmployee
            ? 'Техник является сотрудником автопарка и обслуживает только свой автопарк.'
            : 'Техник является нашим сотрудником и может обслуживать несколько автопарков.'}
        </p>
      </div>
      {/* Форма добавления нового автопарка - только для "наших" техников */}
      {!technic.autoparkEmployee && (
        <div className="technic-autoparks__add-form">
          <h3>Добавить автопарк</h3>
          <div className="technic-autoparks__form-content">
            <select
              className="technic-autoparks__select"
              value={selectedAutoparkId}
              onChange={(e) => setSelectedAutoparkId(e.target.value)}
            >
              <option value="">Выберите автопарк</option>
              {availableAutoparks.map((autopark) => (
                <option key={autopark.id} value={autopark.id}>
                  {autopark.tradeName || autopark.legalName}
                </option>
              ))}
            </select>
            <button
              className="technic-autoparks__add-btn"
              onClick={handleAddAutopark}
              disabled={!selectedAutoparkId || loading}
            >
              Добавить
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="technic-autoparks__loading">Загрузка автопарков...</div>
      ) : error ? (
        <div className="technic-autoparks__error">{error}</div>
      ) : (
        <>
          {/* Список текущих автопарков */}
          <div className="technic-autoparks__content">
            {autoparks.length === 0 ? (
              <div className="technic-autoparks__empty">Нет доступных автопарков</div>
            ) : (
              <table className="technic-autoparks__table">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th>Юридическое название</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {autoparks.map((autopark) => (
                    <tr key={autopark.id}>
                      <td>{autopark.tradeName}</td>
                      <td>{autopark.legalName}</td>

                      <td>
                        {!technic.autoparkEmployee && (
                          <button
                            className="technic-autoparks__remove-btn"
                            onClick={() => handleRemoveAutopark(autopark.id)}
                          >
                            Удалить
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default TechnicAutoparks;
